import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import reportCardApi, { errorMessage } from "./reportCardApi";

/**
 * Settings -> Subject Layout (per class)
 * - Order of subject rows on the report card
 * - Print Row As: subjects with the same text are printed as ONE row (e.g. IP and PE -> "IP/PE")
 * - Show: hide a subject from the report (and from totals)
 */
const SubjectLayoutTab = ({ canEdit }) => {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    reportCardApi.activeClasses().then(setClasses).catch(() => setClasses([]));
  }, []);

  useEffect(() => {
    if (!classId) return setRows([]);
    reportCardApi.subjectLayout.get(classId)
      .then((r) => { setRows(r); setDirty(false); })
      .catch((e) => toast.error(errorMessage(e)));
  }, [classId]);

  const update = (id, patch) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const move = (index, dir) => {
    const next = [...rows];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setRows(next.map((r, i) => ({ ...r, report_sort_order: i + 1 })));
    setDirty(true);
  };

  const save = async () => {
    try {
      const res = await reportCardApi.subjectLayout.save(
        rows.map((r, i) => ({
          id: r.id,
          report_sort_order: r.report_sort_order || i + 1,
          report_row_name: r.report_row_name || "",
          show_on_report: r.show_on_report !== false,
        }))
      );
      toast.success(res.message || "Saved");
      setDirty(false);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  // preview of the printed rows
  const printed = [];
  rows.filter((r) => r.show_on_report !== false).forEach((r) => {
    const name = (r.report_row_name || "").trim() || r.subject_name;
    if (!printed.includes(name)) printed.push(name);
  });

  return (
    <>
      <p className="text-muted">
        Subjects come from <b>Assign Subject</b>. Give two subjects the same <b>Print Row As</b> text (e.g. <code>IP/PE</code>) when a student takes only one of them. They are then printed as one row.
      </p>
      <Row className="g-2 mb-3">
        <Col md={4}>
          <Form.Select value={classId} onChange={(e) => setClassId(e.target.value)}>
            <option value="">-- Select Class --</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.class_name || c.classname}</option>)}
          </Form.Select>
        </Col>
        <Col md={8} className="text-end">
          {canEdit && classId && <Button size="sm" onClick={save} disabled={!dirty}>Save Layout</Button>}
        </Col>
      </Row>

      {classId && (
        <Row>
          <Col lg={8}>
            <Table striped bordered size="sm">
              <thead>
                <tr><th style={{ width: 90 }}>Order</th><th>Subject</th><th>Print Row As</th><th style={{ width: 70 }}>Show</th></tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id}>
                    <td>
                      {canEdit && (
                        <>
                          <Button size="sm" variant="light" className="px-1" disabled={i === 0} onClick={() => move(i, -1)}><i className="fa fa-arrow-up"></i></Button>
                          <Button size="sm" variant="light" className="px-1 ms-1" disabled={i === rows.length - 1} onClick={() => move(i, 1)}><i className="fa fa-arrow-down"></i></Button>
                        </>
                      )}
                    </td>
                    <td>{r.subject_name}</td>
                    <td>
                      <Form.Control size="sm" disabled={!canEdit} placeholder={r.subject_name} value={r.report_row_name || ""} onChange={(e) => update(r.id, { report_row_name: e.target.value })} />
                    </td>
                    <td className="text-center">
                      <Form.Check disabled={!canEdit} checked={r.show_on_report !== false} onChange={(e) => update(r.id, { show_on_report: e.target.checked })} />
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={4} className="text-center text-muted">No subjects assigned to this class</td></tr>}
              </tbody>
            </Table>
          </Col>
          <Col lg={4}>
            <div className="border rounded p-2">
              <b>Report card rows</b>
              <ol className="mb-0 mt-2">{printed.map((p) => <li key={p}>{p}</li>)}</ol>
            </div>
          </Col>
        </Row>
      )}
    </>
  );
};

export default SubjectLayoutTab;
